"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, Loader2, Send, ShieldAlert, Cpu } from "lucide-react";
export default function TechnicalInquiry() {
  return <section id="technical-inquiry" className="dark bg-background text-foreground py-24 px-4 md:px-6 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-xl mb-4 text-primary">
            <Cpu className="size-8" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
            Technický dotaz
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Požádejte o podrobnou technickou cenovou nabídku nebo inženýrskou podporu pro vaše systémy HVAC. Naši specialisté přezkoumají vaše požadavky a poskytnou komplexní řešení.
          </p>
        </div>

        <Card className="border-primary/20 bg-card/60 backdrop-blur-xl shadow-[inset_0_0_40px_rgba(0,153,255,0.08)] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
          
          <CardHeader className="pb-8 border-b border-border/50">
            <CardTitle className="text-2xl flex items-center gap-2">
              Formulář technického dotazu
            </CardTitle>
            <CardDescription className="text-base">
              Uveďte co nejvíce detailů, aby náš inženýrský tým mohl efektivně pomoci.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-8">
            <FormProvider formId="technical_inquiry_form_contact" sectionName="Technical Inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: "",
            emergency: false
          }} validationRules={{
            name: {
              required: "Jméno je povinné"
            },
            email: {
              required: "E‑mail je povinný",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Zadejte platnou e‑mailovou adresu"
              }
            },
            inquiry_type: {
              required: "Vyberte typ dotazu"
            },
            message: {
              required: "Uveďte podrobnosti o vašem dotazu"
            }
          }} submitText="Odeslat technický požadavek" successMessage="Váš technický dotaz byl úspěšně odeslán. Náš inženýrský tým přezkoumá váš požadavek a brzy vás kontaktuje.">
              <InquiryForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/20 rounded-full flex items-center justify-center text-primary mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Žádost přijata</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
      </div>;
  }
  const inputClasses = "flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <FormField control={control} name="emergency" render={({
      field
    }) => <FormItem className="flex flex-row items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 p-5 shadow-sm transition-colors data-[state=checked]:bg-destructive/10">
            <div className="space-y-1.5">
              <FormLabel className="text-base font-semibold flex items-center gap-2 text-destructive">
                <ShieldAlert className="size-5" />
                Vyžadována nouzová služba
              </FormLabel>
              <FormDescription className="text-destructive/80">
                Přepněte, pokud je váš systém zcela nefunkční a vyžaduje okamžitou 24/7 péči.
              </FormDescription>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} className="data-[state=checked]:bg-destructive" />
            </FormControl>
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel>Vaše jméno</FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Jan Novák" {...field} />
              </FormControl>
              <FormDescription>Zadejte jméno osoby podávající dotaz.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel>E‑mailová adresa</FormLabel>
              <FormControl>
                <input type="email" className={inputClasses} placeholder="john@example.com" {...field} />
              </FormControl>
              <FormDescription>Použijeme jej k tomu, abychom vás kontaktovali.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Telefonní číslo (volitelné)</FormLabel>
              <FormControl>
                <input type="tel" className={inputClasses} placeholder="+1 555-123-4567" {...field} />
              </FormControl>
              <FormDescription>Telefonní číslo pro rychlejší následnou komunikaci.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" render={({
        field
      }) => <FormItem>
              <FormLabel>Typ dotazu</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 bg-background/50">
                    <SelectValue placeholder="Vyberte možnost" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Klimatizace", "Tepelná čerpadla", "Větrání", "Jiné"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription>Vyberte systém, se kterým potřebujete pomoc.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Zpráva / Detaily</FormLabel>
            <FormControl>
              <textarea className={`${inputClasses} min-h-[150px] resize-y py-3`} placeholder="Uveďte podrobnosti o technickém problému nebo otázce..." {...field} />
            </FormControl>
            <FormDescription>Uveďte čísla modelů, symptomy nebo jakékoli relevantní informace.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-3 text-destructive">
          <AlertTriangle className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 border-t border-border/50 flex justify-end">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] font-semibold">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-5 animate-spin" />
              Zpracovává se…
            </> : <>
              Odeslat žádost
              <Send className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}